<template>
  <b-overlay :show="isLoading">
    <div v-if="franchise">
      <b-card>
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex align-items-center">
            <template v-if="isEditingTitle">
              <b-form-input
                  v-model="titleDraft"
                  class="mr-1"
                  style="min-width: 280px"
                  @keyup.enter="saveTitle"
              />
              <b-button
                  variant="primary"
                  size="sm"
                  class="mr-50"
                  @click="saveTitle"
              >
                Сохранить
              </b-button>
              <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="isEditingTitle = false"
              >
                Отмена
              </b-button>
            </template>
            <template v-else>
              <h2 class="mb-0 mr-1">
                {{ franchise.title }}
              </h2>
              <b-button
                  variant="flat-secondary"
                  class="btn-icon"
                  size="sm"
                  @click="startEditTitle"
              >
                <feather-icon icon="Edit2Icon"/>
              </b-button>
            </template>
          </div>
          <b-button
              variant="outline-danger"
              size="sm"
              @click="removeFranchise"
          >
            <feather-icon icon="TrashIcon" class="mr-25"/>
            <span>Удалить франшизу</span>
          </b-button>
        </div>
      </b-card>

      <b-row v-if="franchise.movies && franchise.movies.length">
        <b-col
            v-for="movie in franchise.movies"
            :key="movie.id"
            md="2"
        >
          <div class="position-relative">
            <b-badge
                v-if="movie.part_number"
                variant="primary"
                class="franchise-part-badge"
            >
              Часть {{ movie.part_number }}
            </b-badge>
            <movie-card :movie="movie"/>
          </div>
          <div class="d-flex justify-content-center mb-2">
            <span class="text-muted mr-1">{{ movie.year }}</span>
            <b-button
                variant="outline-secondary"
                size="sm"
                class="btn-icon mr-50"
                title="Номер части"
                @click="openPartModal(movie)"
            >
              <feather-icon icon="HashIcon"/>
            </b-button>
            <b-button
                variant="outline-danger"
                size="sm"
                class="btn-icon"
                title="Отвязать от франшизы"
                @click="detachMovie(movie)"
            >
              <feather-icon icon="XIcon"/>
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-card v-else>
        <div class="text-muted">
          В франшизе пока нет проектов — привязывайте фильмы с их деталок
        </div>
      </b-card>

      <b-modal
          v-model="isPartModalVisible"
          title="Номер части"
          centered
          ok-title="Сохранить"
          cancel-title="Отмена"
          @ok="savePartNumber"
      >
        <b-form-group :label="partModalMovie ? partModalMovie.title_ru : ''">
          <b-form-input
              v-model.number="partNumberDraft"
              type="number"
              min="1"
              placeholder="Пусто — без номера"
          />
        </b-form-group>
      </b-modal>
    </div>
  </b-overlay>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BBadge,
  BButton,
  BModal,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'
import {onUnmounted, ref} from '@vue/composition-api'
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import router from '@/router'
import store from '@/store'
import movieStoreModule from '@/views/database/movieStoreModule'
import MovieCard from '@/views/database/MovieCard.vue'

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BBadge,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    MovieCard,
  },
  setup(props, {root}) {
    const STORE_MODULE_NAME = 'movies'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, movieStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const franchise = ref(null)
    const isLoading = ref(false)

    const isEditingTitle = ref(false)
    const titleDraft = ref('')

    const isPartModalVisible = ref(false)
    const partModalMovie = ref(null)
    const partNumberDraft = ref(null)

    const franchiseId = () => router.currentRoute.params.id

    const showError = title => toast({
      component: ToastificationContent,
      props: {title, icon: 'AlertTriangleIcon', variant: 'danger'},
    })

    const fetchFranchise = () => {
      isLoading.value = true
      store.dispatch('movies/fetchFranchise', {id: franchiseId()})
          .then(response => {
            franchise.value = response.data.data
          })
          .catch(() => showError('Ошибка при загрузке франшизы'))
          .finally(() => {
            isLoading.value = false
          })
    }

    const startEditTitle = () => {
      titleDraft.value = franchise.value.title
      isEditingTitle.value = true
    }

    const saveTitle = () => {
      if (!titleDraft.value.trim()) return
      store.dispatch('movies/updateFranchise', {id: franchiseId(), data: {title: titleDraft.value.trim()}})
          .then(response => {
            franchise.value = {...franchise.value, title: response.data.data.title}
            isEditingTitle.value = false
          })
          .catch(() => showError('Ошибка при переименовании'))
    }

    const removeFranchise = async () => {
      const confirmed = await root.$bvModal.msgBoxConfirm(
        `Удалить франшизу «${franchise.value.title}»? Фильмы останутся в базе.`,
        {title: 'Удаление франшизы', okTitle: 'Удалить', cancelTitle: 'Отмена', okVariant: 'danger', centered: true},
      )
      if (!confirmed) return
      store.dispatch('movies/deleteFranchise', {id: franchiseId()})
          .then(() => router.push({name: 'movies'}))
          .catch(() => showError('Ошибка при удалении франшизы'))
    }

    const openPartModal = movie => {
      partModalMovie.value = movie
      partNumberDraft.value = movie.part_number
      isPartModalVisible.value = true
    }

    const savePartNumber = () => {
      store.dispatch('movies/updateFranchiseMovie', {
        franchiseId: franchiseId(),
        movieId: partModalMovie.value.id,
        part_number: partNumberDraft.value || null,
      })
          .then(response => {
            franchise.value = response.data.data
          })
          .catch(() => showError('Ошибка при сохранении номера части'))
    }

    const detachMovie = async movie => {
      const confirmed = await root.$bvModal.msgBoxConfirm(
        `Отвязать «${movie.title_ru}» от франшизы?`,
        {title: 'Отвязка фильма', okTitle: 'Отвязать', cancelTitle: 'Отмена', okVariant: 'danger', centered: true},
      )
      if (!confirmed) return
      store.dispatch('movies/detachFranchiseMovie', {franchiseId: franchiseId(), movieId: movie.id})
          .then(() => fetchFranchise())
          .catch(() => showError('Ошибка при отвязке фильма'))
    }

    fetchFranchise()

    return {
      franchise,
      isLoading,
      isEditingTitle,
      titleDraft,
      startEditTitle,
      saveTitle,
      removeFranchise,
      isPartModalVisible,
      partModalMovie,
      partNumberDraft,
      openPartModal,
      savePartNumber,
      detachMovie,
    }
  },
}
</script>

<style scoped>
.franchise-part-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}
</style>
